// ─── Advisory-voice guard ────────────────────────────────────────────────────
// Suricata describes and never advises (VISION.md). Article headlines are the
// one place where somebody else's voice enters the app: Finnhub syndicates
// opinion columns alongside reporting, and an opinion column is titled things
// like "I'm Buying Broadcom Stock Hand Over Fist". Translated faithfully and
// rendered as the hero line of the insight card, that reads as the app telling
// the user to buy — the exact thing the product forbids.
//
// The classifier prompt is now told to restate such titles descriptively, but a
// prompt is a request, not a guarantee, and it does nothing for the rows
// already in geo_events. So this is the deterministic gate at render time: the
// last check before the text reaches a screen.
//
// It fails safe. A false positive drops one insight; a false negative puts a
// buy recommendation on the home screen. When in doubt the pattern stays in.

// Somebody speaking as an investor in the first person. "קנו"/"מכרו" are
// deliberately absent — they are also plain past tense ("המשקיעים קנו"), which
// is ordinary reporting.
//
// No \b on the Hebrew patterns. JavaScript's \b is defined against ASCII \w, so
// a Hebrew letter is a non-word character on both sides and /\bאני\b/ matches
// nothing at all. Dropping it is also the correct behaviour here: Hebrew glues
// its conjunctions on ("שכדאי", "וקניתי"), and those should match too.
const FIRST_PERSON = [
  /אני\s+(?:קונה|מוכר|משקיע|מחזיק|חושב|מוסיף|מהמר|בוחר|ממליץ|מעדיף)/,
  /(?:קניתי|מכרתי|רכשתי|השקעתי|הוספתי|הגדלתי|הקטנתי)/,
  /למה\s+אני\s/,
  /הייתי\s+(?:קונה|מוכר|משקיע)/,
  /התיק\s+שלי/,
  /\bI(?:'m|’m| am)?\s+(?:buying|selling|betting|loading up)\b/i,
  /\bI\s+(?:bought|sold|own|added)\b/i,
  /\bwhy\s+I\b/i,
  /\bwould\s+I\s+buy\b/i,
  /\bmy\s+(?:top|favorite|favourite|biggest)\s+(?:pick|holding|stock|bet)/i,
];

// Recommendation register, whoever is speaking. The Hebrew list mirrors the
// vocabulary VISION.md bans in the app's own strings.
const RECOMMENDATION = [
  /(?:מומלץ|כדאי|הזדמנות|כדאיות)/,
  /שווה\s+(?:לשקול|לבדוק|קנייה)/,
  /שקול\s+ל/,
  /(?:צריך|חייבים)\s+ל(?:קנות|מכור)/,
  /(?:הגדל|הקטן)\S*\s+חשיפה/,
  /מניות?\s+(?:לקנות|למכירה|מומלצ)/,
  /\bshould\s+you\s+(?:buy|sell|own)\b/i,
  /\bis\s+it\s+time\s+to\s+(?:buy|sell)\b/i,
  /\b(?:buy|sell)\s+(?:now|the\s+dip)\b/i,
  /\bhand\s+over\s+fist\b/i,
  /\bmust[-\s]buy\b/i,
  /\b(?:stocks?|shares?)\s+to\s+(?:buy|own|watch)\b/i,
  /\b(?:best|top)\s+\d*\s*stocks?\b/i,
  /\bno[-\s]brainer\b/i,
  /\bstrong\s+buy\b/i,
];

const PATTERNS = [...FIRST_PERSON, ...RECOMMENDATION];

// True when the text speaks as an investor or tells the reader what to do.
export function hasAdvisoryVoice(text) {
  if (typeof text !== "string" || !text.trim()) return false;
  return PATTERNS.some(re => re.test(text));
}

// The headline to show for a geo_events row, or null when the row has nothing
// that can be shown in a reporting voice.
//
// Order: the Hebrew title, then the factual Hebrew summary, then the original
// title. summary_he is the field the classifier is told to keep free of
// assessment, so it is the natural fallback when a title turns out to be an
// opinion · and it comes before the English original, because a Hebrew row
// that fell through to English is what put Latin headlines on the calendar
// while the same event opened in Hebrew (ROADMAP, "לתיקון", 2026-09-24). Returning
// null is a real outcome and callers must handle it by showing nothing; there
// is no rewriting here, because rewriting somebody's headline would put words
// in the source's mouth.
export function reportedHeadline(ev) {
  if (!ev) return null;
  // No English title among them (26/10/2026): the app shows no English
  // headline, so a row with neither Hebrew field shows nothing.
  const candidates = [ev.headline_he, ev.summary_he];
  for (const c of candidates) {
    const text = typeof c === "string" ? c.trim() : "";
    if (text && !hasAdvisoryVoice(text)) return text;
  }
  return null;
}

// An attributed quotation of an external headline — deliberately NOT gated.
//
// The gate above solved the wrong half of the problem for a headline. Suppressing
// the Hebrew line only fell through to the English one, which is the same
// sentence in another language: nothing was protected, and the app showed a
// Latin headline on a Hebrew screen for its trouble.
//
// What separates advice from reporting here is attribution, not vocabulary. A
// headline is something a named source published, and the caller must render it
// as such — visibly marked as a quotation, with the source name beside it. Under
// that framing the sentence is a fact about what was published, which is exactly
// what the app is for.
//
// The gate stays on everything Suricata writes in its own voice. summary_he is
// therefore NOT a candidate here: it is the classifier's wording, not a quote,
// and presenting it under a "published headline" label would attribute our own
// sentence to somebody else.
//
// Hebrew only (26/10/2026): the app shows no English headline. A row without
// its Hebrew headline has no quotation here, and the event is not shown at
// all until the hourly run translates it (utils/geoEvents.js,
// geo-classify-scheduled.js). `rtl` stays in the answer for the callers.
// Returns { text, rtl } or null.
export function quotedHeadline(ev) {
  const he = typeof ev?.headline_he === "string" ? ev.headline_he.trim() : "";
  return he && /[\u0590-\u05FF]/.test(he) ? { text: he, rtl: true } : null;
}
